import { useEffect, MutableRefObject } from 'react';

import { create } from 'zustand';

type State = {
  scrollTopPositions: Record<string, number | undefined>;
  setScrollTopPosition: (key: string, value: number) => void;
};

export type UseVerticallyScrollablePositionHook = (
  options: UseVerticallyScrollablePositionHookOptions
) => UseVerticallyScrollablePositionHookResult;

export type UseVerticallyScrollablePositionHookOptions = {
  ref: MutableRefObject<HTMLDivElement | null>;
  cacheKey?: string;
};

export type UseVerticallyScrollablePositionHookResult = void;

/**
 * The store that keeps the state of each scrollable.
 *
 * @private
 */
const useStore = create<State>((set) => ({
  scrollTopPositions: {},
  setScrollTopPosition(key: string, value: number): void {
    set((state) => ({
      ...state,
      scrollTopPositions: {
        ...state.scrollTopPositions,
        [key]: value,
      },
    }));
  },
}));

/**
 * Storing and remembering the vertical scroll position of a scrollable.
 * The position is stored in memory and associated using an ID. This for example
 * allows switching between objects on the canvas and returning to the position
 * where the user last left off in the fonts list.
 */
export const useVerticallyScrollablePosition: UseVerticallyScrollablePositionHook =
  ({ ref, cacheKey }) => {
    const scrollTop = useStore((state) =>
      cacheKey ? state.scrollTopPositions[cacheKey] : undefined
    );

    const setScrollTopPosition = useStore(
      (state) => state.setScrollTopPosition
    );

    useEffect(() => {
      const { current } = ref;
      if (!current || typeof scrollTop !== 'number') {
        return;
      }

      current.scrollTo({ top: scrollTop, behavior: 'instant' });
    }, [ref, scrollTop]);

    useEffect(() => {
      const { current } = ref;
      if (!cacheKey || !current) {
        return;
      }

      const onScroll = () => {
        const scrollTop = current.scrollTop;
        if (typeof scrollTop !== 'number') {
          return;
        }

        setScrollTopPosition(cacheKey, scrollTop);
      };

      current.addEventListener('scroll', onScroll, { passive: true });
      return () => current.removeEventListener('scroll', onScroll);
    }, [ref, cacheKey, setScrollTopPosition]);
  };
